import type { CreativeStrategyPackDetail } from '@/services/api';

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[11rem_1fr] gap-3 py-1.5 border-b border-slate-100 text-sm">
      <div className="text-slate-500">{k}</div>
      <div className="text-slate-800 break-all">{v ?? '—'}</div>
    </div>
  );
}

export function PackProvenancePanel({ pack }: { pack: CreativeStrategyPackDetail }) {
  return (
    <div className="max-w-4xl space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-navy-900 mb-2">This version</h3>
        <Row k="Version" v={<span className="font-mono">{pack.version}</span>} />
        <Row k="Generated" v={pack.generated_at} />
        <Row k="Provenance" v={pack.provenance} />
        <Row k="Source run" v={pack.source_run ? <span className="font-mono text-xs">{pack.source_run}</span> : null} />
        <Row k="Scope levels" v={pack.scope_detail.levels?.join(', ')} />
        <Row k="Category paths" v={pack.scope_detail.paths?.map((p) => <div key={p}>{p}</div>)} />
        <Row k="Canon scope key" v={<span className="font-mono">{pack.scope}</span>} />
      </section>

      <section>
        <h3 className="text-sm font-semibold text-navy-900 mb-2">Overrides</h3>
        <Row k="Pinned version" v={pack.pinned_version ?? 'none (follows latest)'} />
        <Row k="Hidden items" v={pack.hidden_count} />
        <Row k="Last changed" v={pack.overrides_updated_at ? `${pack.overrides_updated_at}${pack.overrides_updated_by ? ` by ${pack.overrides_updated_by}` : ''}` : null} />
      </section>

      <section>
        <h3 className="text-sm font-semibold text-navy-900 mb-2">Published versions</h3>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Version</th>
                <th className="text-left px-4 py-2 font-medium">Published</th>
                <th className="text-left px-4 py-2 font-medium">Generated</th>
                <th className="text-left px-4 py-2 font-medium">Corpus client</th>
                <th className="text-right px-4 py-2 font-medium">Concepts</th>
                <th className="text-right px-4 py-2 font-medium">Strategies</th>
                <th className="text-right px-4 py-2 font-medium">Templates</th>
                <th className="text-left px-4 py-2 font-medium">Publisher</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pack.versions.map((v) => (
                <tr key={v.version} className={v.version === pack.version ? 'bg-brand-teal/5' : undefined}>
                  <td className="px-4 py-2 font-mono text-xs">
                    {v.version}
                    {v.is_latest && <span className="ml-2 text-[10px] font-sans text-slate-500">latest</span>}
                    {v.version === pack.pinned_version && <span className="ml-2 text-[10px] font-sans text-brand-teal">pinned</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{v.manifest.published_at?.slice(0, 16).replace('T', ' ') ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{v.manifest.generated_at ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{v.manifest.corpus_client ?? '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{v.manifest.counts?.buyer_concepts ?? '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{v.manifest.counts?.strategies ?? '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{v.manifest.counts?.templates ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 max-w-xs truncate" title={v.manifest.publisher}>{v.manifest.publisher ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Versions are immutable. A re-mine publishes a new folder and moves latest.txt; pinning keeps the angles' citations reproducible.
        </p>
      </section>
    </div>
  );
}
